import React, { useState } from "react";
import { Button, Input, Modal } from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const NewChatModal = ({
  isOpen,
  onClose,
  availableUsers = [],
  onCreateConversation,
}) => {
  const [type, setType] = useState("DIRECT");
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  if (!isOpen) return null;

  const toggleUserSelection = (userId) => {
    if (type === "DIRECT") {
      setSelectedUserIds([userId]);
    } else {
      setSelectedUserIds((prev) =>
        prev.includes(userId)
          ? prev.filter((id) => id !== userId)
          : [...prev, userId]
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedUserIds.length === 0) {
      toast.error("Select at least one participant");
      return;
    }
    if (type === "GROUP" && !groupName.trim()) {
      toast.error("Group name is required");
      return;
    }

    setLoading(true);
    try {
      const participantIds = selectedUserIds.map((id) => {
        const u = availableUsers.find((user) => user._id === id);
        return {
          userId: u._id,
          userModel: u.userModel || "Staff",
        };
      });

      await onCreateConversation({
        type,
        name: groupName,
        description: groupDescription,
        participantIds,
      });

      onClose();
    } catch (err) {
      toast.error("Failed to create conversation: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Start New Conversation"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        

        {/* Chat Type Selection */}
        <div className="flex gap-6 text-xs font-semibold text-neutral-700 dark:text-neutral-200">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="chatType"
              checked={type === "DIRECT"}
              onChange={() => {
                setType("DIRECT");
                setSelectedUserIds([]);
              }}
              className="accent-neutral-900"
            />
            Direct Message
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="chatType"
              checked={type === "GROUP"}
              onChange={() => {
                setType("GROUP");
                setSelectedUserIds([]);
              }}
              className="accent-neutral-900"
            />
            Group Chat
          </label>
        </div>

        {/* Group Inputs */}
        {type === "GROUP" && (
          <div className="space-y-3 pt-1">
            <Input
              type="text"
              label="Group Name *"
              placeholder="Enter group name..."
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              required
            />
            <Input
              type="text"
              label="Group Description (optional)"
              placeholder="What's this group about?"
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
            />
          </div>
        )}

        {/* User Selection List */}
        <div className="space-y-2 pt-1">
          <label className="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
            Select Participants
          </label>
          <div className="max-h-48 overflow-y-auto border border-neutral-200/60 rounded-xl divide-y divide-neutral-100 dark:divide-neutral-800">
            {availableUsers.length === 0 ? (
              <div className="p-4 text-xs text-neutral-400 dark:text-neutral-500 text-center">No available users</div>
            ) : (
              availableUsers.map((user) => {
                const isSelected = selectedUserIds.includes(user._id);
                return (
                  <div
                    key={user._id}
                    onClick={() => toggleUserSelection(user._id)}
                    className={`flex items-center justify-between p-3 cursor-pointer transition-colors text-xs ${
                      isSelected ? "bg-neutral-100/80" : "hover:bg-neutral-50/80"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 border border-neutral-200 dark:border-neutral-800 flex items-center justify-center font-bold text-xs uppercase">
                        {user.name?.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-neutral-900 dark:text-neutral-100">{user.name}</div>
                        <div className="text-[11px] text-neutral-400 dark:text-neutral-500">{user.userModel}</div>
                      </div>
                    </div>
                    <input
                      type={type === "DIRECT" ? "radio" : "checkbox"}
                      checked={isSelected}
                      readOnly
                      className="accent-neutral-900 h-4 w-4"
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-neutral-100 dark:border-neutral-800">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            isLoading={loading}
            className="text-xs font-semibold"
          >
            {loading ? "Creating..." : "Start Chat"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default NewChatModal;